/**
 * 1.3.0 the media portal's app (portal.html), what suppliers see: sign-in with two-step, My deliveries with each
 * assignment's requested items, New upload for open uploads, resumable uploads, the preview of their own file, QC
 * results, comments and decisions, withdraw. Stream P3's: everything under src/portal/ is P3's (P1 builds the sign-in
 * screens in src/portal/identity/).
 *
 * It shares no code with the PRISM app beyond @prism/contracts: its own client (./client.ts, the portal API on its own
 * origin), its own strings (./i18n/en.ts through t()) and its own styles (./portal.css).
 *
 * Routes: / My deliveries, /deliveries/:id one delivery (P2's emails link there), /upload New upload, /uploads My
 * uploads, /submissions/:id a submission's result. Everything else (sign-in, invitations, password reset) is P1's
 * screens, mounted when signed out.
 */
import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, NavLink, Route, Routes } from 'react-router-dom';
import { t } from './i18n/en';
import { errorText, loadSession, portalApi } from './client';
import { Loading, SessionContext } from './components';
import { anyUploadActive } from './uploader';
import { DeliveriesPage, DeliveryPage } from './pages/deliveries';
import { NewUploadPage } from './pages/new-upload';
import { ResultPage, UploadsPage } from './pages/result';
import { AccountPage, PortalSignIn } from './identity/index';
import type { PortalSession } from './types';
import './portal.css';

/** P1's sign-in screens (src/portal/identity/) and the account page. */
const SignIn = PortalSignIn;

function Shell({ session, onSignOut, onChanged }: { session: PortalSession; onSignOut: () => void; onChanged: () => void }) {
  const b = session.branding;
  const name = b?.portalName || t('common.appName');
  const canUpload = session.me ? session.me.canUpload : true;
  useEffect(() => {
    document.title = name;
    if (b?.accentColor) document.documentElement.style.setProperty('--p-accent', b.accentColor);
  }, [name, b?.accentColor]);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => { if (anyUploadActive()) { e.preventDefault(); e.returnValue = t('portal.upload.leaveWarning'); } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  const welcome = b?.welcomeText || t('portal.welcomeDefault', { workspace: session.workspaceName ?? 'PRISM' });
  return (
    <div className="p-shell" data-testid="portal-app">
      <a href="#p-main" className="p-skip">{t('portal.skipToContent')}</a>
      <header className="p-top">
        <div className="p-brand">{b?.logoUrl ? <img src={b.logoUrl} alt="" /> : null}<span>{name}</span></div>
        <nav aria-label={t('portal.nav.label')} className="p-nav">
          <NavLink to="/" end>{t('portal.myDeliveries')}</NavLink>
          {canUpload ? <NavLink to="/upload">{t('portal.newUpload')}</NavLink> : null}
          {canUpload ? <NavLink to="/uploads">{t('portal.myUploads')}</NavLink> : null}
          {session.me ? <NavLink to="/account">{t('identity.account.title')}</NavLink> : null}
        </nav>
        <div className="p-user">
          {session.me ? <span className="p-muted p-small">{t('portal.signedInAs', { name: session.me.displayName, company: session.me.company.name })}</span> : null}
          <button type="button" className="p-btn p-btn-quiet p-btn-small" onClick={onSignOut}>{t('identity.signOut')}</button>
        </div>
      </header>
      <main id="p-main" className="portal">
        <Routes>
          <Route path="/" element={<DeliveriesPage welcome={welcome} />} />
          <Route path="/deliveries/:id" element={<DeliveryPage />} />
          <Route path="/upload" element={<NewUploadPage />} />
          <Route path="/uploads" element={<UploadsPage />} />
          <Route path="/submissions/:id" element={<ResultPage />} />
          {/* 1.3.1: the emails' set-password links (or a stale address after one) while signed in: My deliveries. */}
          <Route path="/invitation" element={<Navigate to="/" replace />} />
          <Route path="/reset" element={<Navigate to="/" replace />} />
          {session.me ? <Route path="/account" element={<AccountPage me={session.me} onSignOut={onSignOut} onChanged={onChanged} />} /> : null}
          <Route path="*" element={<p className="p-empty">{t('portal.notFound')}</p>} />
        </Routes>
      </main>
      {b?.helpText ? (
        <footer className="p-footer">
          <h2>{t('portal.help')}</h2>
          <p className="p-pre">{b.helpText}</p>
        </footer>
      ) : null}
    </div>
  );
}

function PortalApp() {
  const [session, setSession] = useState<PortalSession | 'signed-out' | null>(null);
  const [error, setError] = useState<unknown>(null);
  const load = useCallback(() => {
    setError(null);
    loadSession().then(setSession, setError);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const out = () => setSession('signed-out');
    window.addEventListener('portal:signed-out', out);
    return () => window.removeEventListener('portal:signed-out', out);
  }, []);
  const signOut = async () => {
    await portalApi('/auth/logout', { method: 'POST' }).catch(() => undefined);
    setSession('signed-out');
  };
  if (error) return <main className="portal"><p className="p-bad" role="alert">{errorText(error)}</p><button type="button" className="p-btn" onClick={load}>{t('common.retry')}</button></main>;
  if (!session) return <main className="portal"><Loading /></main>;
  if (session === 'signed-out') return <main className="portal"><SignIn onSignedIn={load} /></main>;
  return (
    <SessionContext.Provider value={session}>
      <Shell session={session} onSignOut={() => void signOut()} onChanged={load} />
    </SessionContext.Provider>
  );
}

createRoot(document.getElementById('portal')!).render(<StrictMode><BrowserRouter><PortalApp /></BrowserRouter></StrictMode>);
